import { logEvent, setUserId, setUserProperties, setCurrentScreen } from 'firebase/analytics';
import { analytics } from '../firebase/config';

// Analytics utility functions
export const trackEvent = (eventName, parameters = {}) => {
  if (analytics) {
    try {
      logEvent(analytics, eventName, parameters);
      console.log('Analytics event tracked:', eventName, parameters);
    } catch (error) {
      console.error('Error tracking analytics event:', error);
    }
  }
};

// Track page views
export const trackPageView = (pageName, pageTitle = '') => {
  if (analytics) {
    try {
      // Set current screen for Firebase Analytics
      setCurrentScreen(analytics, pageName);
      
      // Track page view event
      trackEvent('page_view', {
        page_name: pageName,
        page_title: pageTitle,
        page_location: window.location.href,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }
};

// Track user interactions
export const trackUserAction = (action, category = 'user_interaction', label = '', value = null) => {
  trackEvent('user_action', {
    action: action,
    category: category,
    label: label,
    value: value
  });
};

// Track quote-related events
export const trackQuoteEvent = (eventType, quoteData = {}) => {
  trackEvent('quote_event', {
    event_type: eventType,
    quote_frequency: quoteData.serviceFrequency,
    quote_amount: quoteData.quoteAmount,
    services_count: Object.values(quoteData.selectedServices || {}).filter(Boolean).length,
    property_address: quoteData.address ? 'provided' : 'not_provided'
  });
};

// Track form interactions
export const trackFormEvent = (formName, eventType, fieldName = '') => {
  trackEvent('form_event', {
    form_name: formName,
    event_type: eventType,
    field_name: fieldName
  });
};

// Track conversion events
export const trackConversion = (conversionType, value = null, currency = 'USD') => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value,
    currency: currency
  });
};

// Track admin dashboard events
export const trackAdminEvent = (action, details = {}) => {
  trackEvent('admin_action', {
    action: action,
    ...details
  });
};

// Set user properties
export const setUserAnalyticsProperties = (userId, properties = {}) => {
  if (analytics) {
    try {
      setUserId(analytics, userId);
      setUserProperties(analytics, properties);
      console.log('User analytics properties set:', userId, properties);
    } catch (error) {
      console.error('Error setting user analytics properties:', error);
    }
  }
};

// Track e-commerce events
export const trackPurchase = (transactionId, value, currency = 'USD', items = []) => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency,
    items: items
  });
};

// Track service selection
export const trackServiceSelection = (serviceName, serviceType, isSelected) => {
  trackEvent('service_selection', {
    service_name: serviceName,
    service_type: serviceType,
    is_selected: isSelected,
    timestamp: new Date().toISOString()
  });
};

// Track address selection
export const trackAddressSelection = (address, source = 'manual') => {
  trackEvent('address_selection', {
    address_provided: !!address,
    address_length: address ? address.length : 0,
    source: source,
    timestamp: new Date().toISOString()
  });
};

// Track session duration
export const trackSessionStart = () => {
  trackEvent('session_start', {
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    screen_resolution: `${screen.width}x${screen.height}`
  });
};

// Track session end
export const trackSessionEnd = (duration) => {
  trackEvent('session_end', {
    duration: duration,
    timestamp: new Date().toISOString()
  });
};

// Track error events
export const trackError = (errorType, errorMessage, errorLocation) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    error_location: errorLocation,
    timestamp: new Date().toISOString()
  });
};

// Track performance metrics
export const trackPerformance = (metricName, value, unit = 'ms') => {
  trackEvent('performance_metric', {
    metric_name: metricName,
    value: value,
    unit: unit,
    timestamp: new Date().toISOString()
  });
};

// Common event names for consistency
export const ANALYTICS_EVENTS = {
  // Page views
  PAGE_VIEW: 'page_view',
  
  // User actions
  BUTTON_CLICK: 'button_click',
  LINK_CLICK: 'link_click',
  FORM_START: 'form_start',
  FORM_COMPLETE: 'form_complete',
  FORM_ABANDON: 'form_abandon',
  
  // Quote events
  QUOTE_START: 'quote_start',
  QUOTE_SUBMIT: 'quote_submit',
  QUOTE_SUCCESS: 'quote_success',
  QUOTE_ERROR: 'quote_error',
  
  // Admin events
  ADMIN_LOGIN: 'admin_login',
  ADMIN_QUOTE_VIEW: 'admin_quote_view',
  ADMIN_QUOTE_EDIT: 'admin_quote_edit',
  ADMIN_QUOTE_DELETE: 'admin_quote_delete',
  ADMIN_BULK_ACTION: 'admin_bulk_action',
  
  // User management events
  USER_UPDATED: 'user_updated',
  USER_DELETED: 'user_deleted',
  USER_STATUS_CHANGED: 'user_status_changed',
  
  // Settings events
  SETTINGS_UPDATED: 'settings_updated',
  
  // Conversions
  QUOTE_CONVERSION: 'quote_conversion',
  CONTACT_CONVERSION: 'contact_conversion'
};

// Common parameters for consistency
export const ANALYTICS_PARAMS = {
  // User info
  USER_EMAIL: 'user_email',
  USER_PHONE: 'user_phone',
  
  // Quote info
  QUOTE_FREQUENCY: 'quote_frequency',
  QUOTE_AMOUNT: 'quote_amount',
  SERVICES_COUNT: 'services_count',
  PROPERTY_ADDRESS: 'property_address',
  
  // Form info
  FORM_NAME: 'form_name',
  FIELD_NAME: 'field_name',
  
  // Page info
  PAGE_NAME: 'page_name',
  PAGE_TITLE: 'page_title',
  PAGE_LOCATION: 'page_location'
};
